import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../utils/supabase';
import { analyticsApi, whatsappApi } from '../utils/api';
import { 
  ChartBarIcon, 
  ChatBubbleLeftRightIcon, 
  UserGroupIcon, 
  ClockIcon,
  CurrencyDollarIcon,
  ShoppingBagIcon,
  TruckIcon,
  PhoneIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlusIcon,
  EyeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
// Import recharts pour les graphiques
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
import { getDeviseActive } from '../utils/devise';
// Styles du dashboard pilote : surfaces, cartes et animations limitées à cette page.
import './Dashboard.css';

/**
 * Affiche une statistique déjà calculée par le dashboard, avec son icône et sa variation.
 * La couleur reçue garde le repère financier existant (vert ou rouge) sans ajouter de logique métier.
 * Une éventuelle action conserve exactement son libellé et son gestionnaire d'origine.
 * Déclarer la carte hors du dashboard évite de la remonter à chaque actualisation.
 */
const StatCard = ({ title, value, icon: Icon, growth, color = 'blue', action }) => (
  <div className={`dashboard-stat dashboard-stat--${color}`}>
    <dt className="dashboard-stat-heading">
      <span>{title}</span>
      <span className="dashboard-stat-icon"><Icon aria-hidden="true" /></span>
    </dt>
    <dd className="dashboard-stat-value">{value}</dd>
    {/* Convertit la condition en booléen pour éviter un « 0 » isolé quand aucune variation n'est affichée. */}
    {Boolean(growth) && (
      <div className="dashboard-stat-growth">
        {growth > 0
          ? <ArrowTrendingUpIcon aria-hidden="true" className="h-4 w-4 text-green-700" />
          : <ArrowTrendingDownIcon aria-hidden="true" className="h-4 w-4 text-red-700" />}
        <span className={growth > 0 ? 'text-green-700' : 'text-red-700'}>{Math.abs(growth)}%</span>
        <span>vs mois dernier</span>
      </div>
    )}
    {action && (
      <button type="button" onClick={action.onClick} className="dashboard-stat-action">
        {action.label}
      </button>
    )}
  </div>
);

/**
 * Présente un raccourci sous forme de carte cliquable avec une description lisible.
 * Le gestionnaire reçu reste responsable de la navigation ; ce composant ne change aucune route.
 * L'icône et la flèche sont décoratives, car le titre nomme déjà l'action pour les lecteurs d'écran.
 */
const QuickAction = ({ title, description, icon: Icon, onClick }) => (
  <button type="button" onClick={onClick} className="dashboard-quick-action">
    <span className="dashboard-quick-icon"><Icon aria-hidden="true" /></span>
    <span className="dashboard-quick-text">
      <span className="dashboard-quick-title">{title}</span>
      <span className="dashboard-quick-description">{description}</span>
    </span>
    <ArrowTrendingUpIcon aria-hidden="true" className="dashboard-quick-arrow" />
  </button>
);

/**
 * Affiche les indicateurs, les graphiques et les raccourcis de la boutique.
 * Les appels API et les calculs existants sont conservés ; les groupes visuels apparaissent
 * progressivement après le chargement des données, dans l'ordre du pattern vidéo.
 */
const Dashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    totalClients: 0, // Ajout des clients
    activeDeliveries: 0,
    whatsappStatus: 'disconnected',
    revenueGrowth: 0,
    ordersGrowth: 0,
    clientsGrowth: 0, // Ajout de la croissance des clients
    deliveriesGrowth: 0,
    netProfit: 0, // Profit net (revenus - dépenses)
    totalExpenses: 0, // Total des dépenses
    profitMargin: 0, // Marge bénéficiaire en %
    totalShippingFees: 0 // Frais de livraison (reversés aux livreurs, affiché séparément)
  });
  
  const [revenueChartData, setRevenueChartData] = useState(null);
  const [ordersChartData, setOrdersChartData] = useState(null);
  const [topProductsData, setTopProductsData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  /**
   * Charge les indicateurs agrégés, le statut WhatsApp et la rentabilité du mois.
   * Transforme les réponses existantes en séries pour les graphiques et en activité récente.
   * Les services optionnels conservent leur repli ; le chargement se termine dans tous les cas.
   */
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Récupération des données agrégées du dashboard
      const dashboardResponse = await analyticsApi.getDashboard();
      const dashboardData = dashboardResponse?.data || {};

      // Statut WhatsApp (non inclus dans le dashboard backend)
      let whatsappStatus = 'disconnected';
      try {
        const statusResponse = await whatsappApi.getStatus();
        whatsappStatus = statusResponse?.data?.status?.toLowerCase() || 'disconnected';
      } catch (error) {
        console.log('WhatsApp API non disponible');
      }

      // Récupération des données de rentabilité
      let profitabilityData = null;
      try {
        const now = new Date();
        const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const profitResponse = await analyticsApi.getProfitability({
          start_date: firstDayThisMonth.toISOString(),
          end_date: now.toISOString()
        });
        profitabilityData = profitResponse?.data || null;
      } catch (error) {
        console.log('Données de rentabilité non disponibles');
      }

      // Remplissage des stats venant du backend
      if (dashboardData.stats) {
        setStats({
          ...dashboardData.stats,
          totalClients: dashboardData.stats.totalClients || 0,
          clientsGrowth: dashboardData.stats.clientsGrowth || 0,
          whatsappStatus,
          // Ajouter les données de rentabilité si disponibles
          netProfit: profitabilityData?.netProfit || 0,
          totalExpenses: profitabilityData?.totalExpenses || 0,
          profitMargin: profitabilityData?.profitMargin || 0,
          // Frais de livraison (reversés aux livreurs, affiché séparément)
          totalShippingFees: profitabilityData?.totalShippingFees || 0
        });
      }

      // ========= Graphiques =========
      // Format recharts: tableau d'objets [{ date, revenue }, ...]
      if (dashboardData.revenueLast7Days) {
        const chartData = dashboardData.revenueLast7Days.map(item => ({
          date: dayjs(item.date).format('DD/MM'),
          revenue: item.total_revenue || 0
        }));
        setRevenueChartData(chartData);
      }

      // Format recharts: tableau d'objets [{ date, orders }, ...]
      if (dashboardData.ordersLast7Days) {
        const chartData = dashboardData.ordersLast7Days.map(item => ({
          date: dayjs(item.date).format('DD/MM'),
          orders: item.total_orders || 0
        }));
        setOrdersChartData(chartData);
      }

      // ========= Top produits =========
      // Format recharts: tableau d'objets [{ name, value, color }, ...]
      if (dashboardData.topProducts) {
        const colors = [
          '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
          '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#06B6D4'
        ];
        const chartData = dashboardData.topProducts.map((p, index) => ({
          name: p.product_name,
          value: p.total_revenue || 0,
          color: colors[index % colors.length]
        }));
        setTopProductsData(chartData);
      }

      // ========= Activité récente =========
      if (dashboardData.recentOrders) {
        const activities = dashboardData.recentOrders.map(order => ({
          id: order.id,
          type: 'order',
          title: `Commande #${order.order_number || order.id}`,
          description: `${order.total_amount || 0} ${getDeviseActive()}`,
          time: order.created_at,
          status: order.status,
        }));
        setRecentActivity(activities);
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  /** Charge une fois les données au montage de la page ; une saisie ou un rendu visuel ne relance pas les requêtes. */
  useEffect(() => {
    loadDashboardData();
  }, []);

  /** Formate un montant avec les séparateurs français, sans décimales ; l'unité (devise de la boutique) est ajoutée par l'affichage. */
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="dashboard-page dashboard-loading" role="status" aria-live="polite">
        {/* Le libellé reste visible même lorsque le mouvement réduit désactive le spinner. */}
        <span className="dashboard-loading-spinner" aria-hidden="true" />
        <p>Chargement du tableau de bord...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-page-inner">
        {/* Premier groupe : titre et contexte, puis les indicateurs suivent avec un délai court. */}
        <header className="dashboard-page-header dashboard-enter dashboard-enter--header">
          <div>
            <p className="dashboard-eyebrow">Mercuri Shop · Vue d'ensemble</p>
            <h1>Tableau de bord</h1>
            <p className="dashboard-page-subtitle">Vue d'ensemble de votre activité</p>
          </div>
          <span className="dashboard-period">Ce mois-ci</span>
        </header>

        {/* Métriques principales avec espacement amélioré */}
        <dl className="dashboard-metrics dashboard-enter dashboard-enter--metrics">
          <StatCard
            title="Revenus du mois"
            value={`${formatCurrency(stats.totalRevenue)} ${getDeviseActive()}`}
            icon={CurrencyDollarIcon}
            growth={stats.revenueGrowth}
            color="green"
          />
          <StatCard
            title="Commandes"
            value={stats.totalOrders}
            icon={ShoppingBagIcon}
            growth={stats.ordersGrowth}
            color="blue"
          />
          <StatCard
            title="Clients"
            value={stats.totalClients}
            icon={UserGroupIcon}
            growth={stats.clientsGrowth}
            color="purple"
          />

          <StatCard
            title="WhatsApp"
            value={stats.whatsappStatus === 'connected' ? 'Connecté' : 'Déconnecté'}
            icon={PhoneIcon}
            color={stats.whatsappStatus === 'connected' ? 'green' : 'red'}
            action={{
              label: stats.whatsappStatus === 'connected' ? 'Voir' : 'Connecter',
              onClick: () => window.location.href = '/app/settings'
            }}
          />
        </dl>

        {/* Section Rentabilité - Nouvelle section */}
        <section className="dashboard-profitability dashboard-enter dashboard-enter--profitability">
          <h2 className="dashboard-section-heading">
            <CurrencyDollarIcon aria-hidden="true" className="h-5 w-5" />
            Rentabilité du mois
          </h2>
          <dl className="dashboard-metrics">
            <StatCard
              title="Profit Net"
              value={`${formatCurrency(stats.netProfit)} ${getDeviseActive()}`}
              icon={stats.netProfit >= 0 ? ArrowTrendingUpIcon : ArrowTrendingDownIcon}
              color={stats.netProfit >= 0 ? 'green' : 'red'}
            />
            <StatCard
              title="Dépenses"
              value={`${formatCurrency(stats.totalExpenses)} ${getDeviseActive()}`}
              icon={CurrencyDollarIcon}
              color="red"
            />
            <StatCard
              title="Frais Livraison"
              value={`${formatCurrency(stats.totalShippingFees)} ${getDeviseActive()}`}
              icon={TruckIcon}
              color="purple"
            />
            <StatCard
              title="Marge Bénéficiaire"
              value={`${stats.profitMargin}%`}
              icon={ChartBarIcon}
              color={stats.profitMargin >= 20 ? 'green' : stats.profitMargin >= 10 ? 'purple' : 'red'}
            />
          </dl>
        </section>

        {/* Graphiques avec design amélioré */}
        <div className="dashboard-charts dashboard-enter dashboard-enter--charts">
          {/* Graphique des revenus avec style premium */}
          <div className="dashboard-card dashboard-card--revenue">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Revenus des 7 derniers jours</h3>
              <div className="dashboard-card-icon">
                <ChartBarIcon aria-hidden="true" className="h-4 w-4" />
              </div>
            </div>
            {revenueChartData && (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueChartData}>

                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(107, 114, 128, 0.08)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickFormatter={(value) => `${formatCurrency(value)}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        border: '1px solid #151519',
                        borderRadius: 8,
                        color: 'white'
                      }}
                      formatter={(value) => [`${formatCurrency(value)} ${getDeviseActive()}`, 'Revenu']}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#151519"
                      strokeWidth={3}
                      fill="#cef34a"
                      fillOpacity={0.25}
                      isAnimationActive={false}
                      dot={{ fill: '#151519', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#ffffff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Top produits avec style moderne */}
          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Top Produits</h3>
              <div className="dashboard-card-icon">
                <SparklesIcon aria-hidden="true" className="h-4 w-4" />
              </div>
            </div>
            {topProductsData && (
              <div className="h-64 flex flex-col">
                {/* Graphique PieChart */}
                <div className="h-40 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={topProductsData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={65}
                        dataKey="value"
                        nameKey="name"
                        paddingAngle={2}
                        isAnimationActive={false}
                      >
                        {topProductsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          border: 'none',
                          borderRadius: 8,
                          color: 'white'
                        }}
                        formatter={(value, name) => [`${formatCurrency(value)} ${getDeviseActive()}`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Légende personnalisée avec noms tronqués */}
                <div className="flex-1 overflow-y-auto mt-2 space-y-1">
                  {topProductsData.slice(0, 5).map((entry, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span
                        className="text-gray-600 truncate"
                        title={entry.name}
                      >
                        {entry.name.length > 25 ? `${entry.name.substring(0, 25)}...` : entry.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Commandes par jour et activité récente avec style amélioré */}
        <div className="dashboard-detail-grid dashboard-enter dashboard-enter--details">
          {/* Graphique des commandes */}
          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Commandes par jour</h3>
              <div className="dashboard-card-icon">
                <ShoppingBagIcon aria-hidden="true" className="h-4 w-4" />
              </div>
            </div>
            {ordersChartData && (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ordersChartData}>

                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(107, 114, 128, 0.08)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#e5e7eb' }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        border: 'none',
                        borderRadius: 8,
                        color: 'white'
                      }}
                      formatter={(value) => [value, 'Commandes']}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Bar
                      dataKey="orders"
                      fill="#cef34a"
                      isAnimationActive={false}
                      stroke="#687d20"
                      strokeWidth={1}
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Activité récente modernisée */}
          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Activité récente</h3>
              <div className="dashboard-card-icon">
                <ClockIcon aria-hidden="true" className="h-4 w-4" />
              </div>
            </div>
            <div className="dashboard-activity-list">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={activity.id} className="dashboard-activity-row">
                    <div className="flex-shrink-0">
                      <div className="dashboard-activity-icon">
                        <ShoppingBagIcon aria-hidden="true" className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <p className="dashboard-activity-title">{activity.title}</p>
                      <p className="dashboard-secondary text-xs">{activity.description}</p>
                    </div>
                    <div className="dashboard-activity-time">
                      {dayjs(activity.time).format('DD/MM HH:mm')}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <ClockIcon className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="dashboard-empty-text text-sm">Aucune activité récente</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dernier groupe : les raccourcis gardent leurs destinations existantes. */}
        <div className="dashboard-card dashboard-actions dashboard-enter dashboard-enter--actions">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Actions rapides</h3>
            <div className="dashboard-card-icon">
              <SparklesIcon aria-hidden="true" className="h-5 w-5" />
            </div>
          </div>
          <div className="dashboard-quick-grid">
            <QuickAction
              title="Nouvelle commande"
              description="Créer une commande"
              icon={PlusIcon}
              onClick={() => window.location.href = '/app/orders'}
              color="blue"
            />
            <QuickAction
              title="Gérer produits"
              description="Ajouter/modifier produits"
              icon={ShoppingBagIcon}
              onClick={() => window.location.href = '/app/products'}
              color="green"
            />
            <QuickAction
              title="Voir analytics"
              description="Statistiques détaillées"
              icon={ChartBarIcon}
              onClick={() => window.location.href = '/app/analytics'}
              color="purple"
            />

          </div>
        </div>
      </div>


    </div>
  );
};

export default Dashboard;
